// src/components/routes/RouteGrid.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, CardTitle, Button,
    Table, Modal, ModalHeader, ModalBody, Spinner, Row, Col, FormGroup, Label, Input
} from "reactstrap";
import Session from "../auth/Session";
import AuthFunctions from "../services/AuthService";
import SearchableDropdownFilter from "./SearchableDropdownFilter";
import { getFilters, saveFilter } from "./filterStorage";
import RouteForm from "./RouteForm";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const getCustomerLabel = (customer) => customer.name;
const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;
const getOptionId = (option) => option.id;

const RouteGrid = () => {
    const apiBaseUrl = "/api/routes";
    const subCustomersApi = "/api/subcustomers";
    const customersApi = "/api/customers";

    const [isAdmin, setIsAdmin] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerId, setCustomerId] = useState(() => getFilters().customerId || "");
    const [subCustomers, setSubCustomers] = useState([]);
    const [selectedSubCustomerId, setSelectedSubCustomerId] = useState(() => getFilters().subCustomerId || "");
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);

    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const token = await Session.getToken();
                const admin = await AuthFunctions.getIsAdminAsync();
                setIsAdmin(admin);

                const requests = [fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } })];
                if (admin) requests.push(fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } }));

                const [scRes, cRes] = await Promise.all(requests);
                setSubCustomers(scRes.ok ? await scRes.json() : []);
                if (cRes) setCustomers(cRes.ok ? await cRes.json() : []);
            } catch (e) {
                console.error("Failed to load filter data", e);
            }
        })();
    }, []);

    const availableSubCustomers = useMemo(() => {
        if (!isAdmin || !customerId) return subCustomers;
        return subCustomers.filter(sc => `${sc.customerId}` === `${customerId}`);
    }, [isAdmin, subCustomers, customerId]);

    useEffect(() => {
        if (!selectedSubCustomerId) {
            setRoutes([]);
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const token = await Session.getToken();
                const res = await fetch(
                    `${apiBaseUrl}/BySubCustomer/${selectedSubCustomerId}?includeInactive=${includeInactive}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setRoutes(res.ok ? await res.json() : []);
            } catch (e) {
                console.error("Failed to load routes", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedSubCustomerId, includeInactive]);

    const reloadRoutes = async () => {
        if (!selectedSubCustomerId) return;
        setLoading(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(
                `${apiBaseUrl}/BySubCustomer/${selectedSubCustomerId}?includeInactive=${includeInactive}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setRoutes(res.ok ? await res.json() : []);
        } finally {
            setLoading(false);
        }
    };

    // Map each parent route id to the names of its sub-routes, and each route id to its name,
    // so the grid can show which routes belong to a parent (and each child's parent).
    const routeNameById = useMemo(
        () => new Map(routes.map(r => [r.id, r.name])),
        [routes]
    );
    const childrenByParentId = useMemo(() => {
        const map = new Map();
        for (const r of routes) {
            if (r.parentRouteId) {
                if (!map.has(r.parentRouteId)) map.set(r.parentRouteId, []);
                map.get(r.parentRouteId).push(r.name);
            }
        }
        return map;
    }, [routes]);

    const sortGetters = useMemo(() => ({
        driver: (r) => r.driver?.driverName ?? r.driverId,
        // Mirror the string shown in the Parent / Sub-Routes cell so sorting matches.
        parentInfo: (r) => {
            if (r.parentRouteId) return `Sub-route of ${routeNameById.get(r.parentRouteId) ?? `#${r.parentRouteId}`}`;
            const children = childrenByParentId.get(r.id);
            if (children && children.length > 0) return `Parent ${children.join(", ")}`;
            if (r.isParentRoute) return "Parent";
            return "";
        },
        quantity: (r) => Number(r.quantity),
        billingRate: (r) => parseFloat(r.billingRate),
        payRate: (r) => parseFloat(r.payRate),
        transactionType: (r) => r.transactionType?.transactionName,
    }), [routeNameById, childrenByParentId]);

    const { items: sortedRoutes, sortConfig, requestSort } = useSortableData(
        routes,
        { key: "name", direction: "asc", getters: sortGetters }
    );

    const handleSaved = () => {
        setOpen(false);
        reloadRoutes();
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5" className="mb-0">Route Management</CardTitle>
                        <Button
                            color="primary"
                            onClick={() => { setEditing(null); setOpen(true); }}
                            disabled={!selectedSubCustomerId}
                            title={!selectedSubCustomerId ? "Select a Sub-Customer first" : ""}
                        >
                            + New Route
                        </Button>
                    </div>

                    <Row className="mb-3">
                        {isAdmin && (
                            <Col md={4}>
                                <SearchableDropdownFilter
                                    value={customerId}
                                    options={customers}
                                    placeholder="Select Customer..."
                                    getOptionValue={getOptionId}
                                    getOptionLabel={getCustomerLabel}
                                    onChange={(value) => {
                                        setCustomerId(value);
                                        setSelectedSubCustomerId("");
                                        saveFilter("customerId", value);
                                        saveFilter("subCustomerId", "");
                                        setRoutes([]);
                                    }}
                                />
                            </Col>
                        )}
                        <Col md={isAdmin ? 4 : 6}>
                            <SearchableDropdownFilter
                                value={selectedSubCustomerId}
                                options={availableSubCustomers}
                                placeholder="Select Sub-Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getSubCustomerLabel}
                                onChange={(value) => {
                                    setSelectedSubCustomerId(value);
                                    saveFilter("subCustomerId", value);
                                }}
                                disabled={isAdmin && !customerId}
                            />
                        </Col>
                        <Col md={isAdmin ? 4 : 6} className="d-flex align-items-center">
                            <FormGroup check className="mb-0">
                                <Label check>
                                    <Input
                                        id="includeInactive"
                                        type="checkbox"
                                        checked={includeInactive}
                                        onChange={(e) => setIncludeInactive(e.target.checked)}
                                    />{" "}
                                    Show inactive routes
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>

                    {!selectedSubCustomerId ? (
                        <div className="text-muted">Select a sub-customer to view routes.</div>
                    ) : loading ? (
                        <div className="py-5 text-center"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Driver" sortKey="driver" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Parent / Sub-Routes" sortKey="parentInfo" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Qty" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Billing Rate" sortKey="billingRate" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Pay Rate" sortKey="payRate" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Txn Type" sortKey="transactionType" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRoutes.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center">No routes found</td></tr>
                                ) : sortedRoutes.map(r => {
                                    const children = childrenByParentId.get(r.id);
                                    return (
                                    <tr key={r.id} className={r.isActive === false ? 'text-muted fst-italic' : ''}>
                                        <td>{r.name}</td>
                                        <td>{r.driver?.driverName ?? r.driverId}</td>
                                        <td>
                                            {r.parentRouteId ? (
                                                <span className="text-muted">↳ Sub-route of {routeNameById.get(r.parentRouteId) ?? `#${r.parentRouteId}`}</span>
                                            ) : children && children.length > 0 ? (
                                                <><span className="badge bg-secondary me-1">Parent</span>{children.join(', ')}</>
                                            ) : r.isParentRoute ? (
                                                <span className="badge bg-secondary">Parent</span>
                                            ) : ""}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{r.quantity}</td>
                                        <td style={{ textAlign: 'right' }}>${parseFloat(r.billingRate).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right' }}>${parseFloat(r.payRate).toFixed(2)}</td>
                                        <td style={{ textAlign: 'right' }}>{r.transactionType?.transactionName}</td>
                                        <td className="text-end">
                                            <Button size="sm" color="info" onClick={() => { setEditing(r); setOpen(true); }}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
                <ModalHeader toggle={() => setOpen(false)}>
                    {editing ? "Edit Route" : "New Route"}
                </ModalHeader>
                <ModalBody>
                    <RouteForm
                        routeItem={editing ?? { id: 0, subCustomerId: selectedSubCustomerId }}
                        apiBaseUrl={apiBaseUrl}
                        onSuccess={handleSaved}
                        onCancel={() => setOpen(false)}
                        routes={routes}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default RouteGrid;
